import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Sticky from 'react-sticky-el';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { Box, Col, Row, VStack } from '@saucelabs/sl-component-grid';
import { Button } from '@saucelabs/ds-button';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { IconManualOutline, IconAutomatedOutline } from '@saucelabs/ds-icons';
import { ContentLoader, Loader } from '@saucelabs/sl-component-loader';
import { Typography } from '@saucelabs/ds-typography';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';
import { STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';
import { records as userRecords } from '@saucelabs/sl-module-user';

import {
  ADDON_PROJECTS_PAID_THRESHOLD,
  ALLOWED_CONCURRENCY_VALUES,
  CONCURRENCY_KEY,
  INTERVAL_KEY,
  PROJECTS_KEY,
  CROSS_BROWSER_FEATURES_MODAL,
  FADE,
  JUMP_FADE,
  MOBILE_QUALITY_COMPARISON_MODAL,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_AUTOMATED,
  PRODUCT_LIVE,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_RDC,
  PRODUCT_VDC,
  ROUTE_PARENT_SECTION,
  STICKY_SUMMARY_OFFSET,
  TESTING_TYPES,
  TRANSITION,
  NUDGE_ANNUAL_PLAN_MODAL,
  ROUTE_SECTION_CHECKOUT,
} from '../constants';
import { Plan } from '../records/Plan';
import {
  PlanConfiguration,
} from '../records/PlanConfiguration';
import { Subscription } from '../records/Subscription';
import BackToPlatformLink from '../components/BackToPlatformLink';
import GridContainer from '../components/Layout/GridContainer';
import { SectionTitle } from '../components/Layout/SectionTitle';
import { OrderSummary } from '../components/OrderSummary';
import { Tab, Tabs } from '../components/Layout/Tabs';
import { Product } from '../components/Purchase/Product';
import { EnterprisePlansTile } from '../components/Purchase/ProductTiles/EnterprisePlansTile';
import { OpenSauceTile } from '../components/Purchase/ProductTiles/OpenSauceTile';
import { UnifiedPlan } from '../components/Purchase/SubscriptionBoxes/UnifiedPlan';
import { LegacyPlan } from '../components/Purchase/SubscriptionBoxes/LegacyPlan';
import { LiveTile } from '../components/Purchase/ProductTiles/LiveTile';
import { PlansTitle } from '../components/Purchase/ProductTiles/PlansTitle';
import { MobileQualityTile } from '../components/Purchase/ProductTiles/MobileQualityTile';
import { AutomatedTile } from '../components/Purchase/ProductTiles/AutomatedTile';
import { ApiTestingTile } from '../components/Purchase/ProductTiles/ApiTestingTile';
import { actions as invoicesActions } from '../ducks/invoices';
import {
  operations as billingInfoOperations,
  selectors as billingInfoSelectors,
} from '../ducks/billing-info';
import { selectors as plansSelectors } from '../ducks/plans';
import { actions, operations, selectors } from '../ducks/purchase';
import { selectors as subscriptionsSelectors } from '../ducks/subscriptions';
import LayoutStyles from './../components/Layout/LayoutGeneral.styles';
import TabsStyles from './../components/Layout/Tabs.styles';
import OrderSummaryStyles from './../components/OrderSummary.styles';
import {
  EVENTS,
  MODE_CONCURRENCY,
  MODE_USAGE,
  PRODUCT_AREA,
  PRODUCT_SUB_AREAS,
  SUBJECT,
  trackEvent,
} from '../tracking';
import Styles from '../components/PurchaseContainer.styles';
import CrossBrowserFeaturesModal from '../components/CrossBrowserFeaturesModal';
import MobileQualityComparisonModal from '../components/MobileQualityComparisonModal';
import SideNavigation from '../components/SideNavigation';
import { getIntervalUnit } from './../utils';
import { getMonthlyPlans } from '../ducks/plans/utils';
import UsagePurchaseContainer from './UsagePurchaseContainer';

const PARENT_CRUMB = {
  name: 'Subscription and Billing',
  url: `/${ROUTE_PARENT_SECTION}`,
  onClick: () => trackEvent(EVENTS.PLANS_NAVIGATE_BACK, { checkout_step: 'overview' }),
};

const PurchaseContainer = ({
  allPlans,
  apiTestingConfig,
  apiTestingPlans,
  billingInfo,
  clearPurchaseData,
  currentMonthlyRate,
  currentWebAndMobileTab,
  ensureAccountPresent,
  fetchInvoices,
  hasInvoices,
  hideModal,
  initialize,
  initialized,
  isContinueBlocked,
  legacySubscriptions,
  liveConfig,
  livePlans,
  mobileQualityConfig,
  mobileQualityPlans,
  orderItems,
  persistPurchaseData,
  prices,
  rdcConfig,
  rdcPlans,
  removeCrossBrowserConfig,
  setWebAndMobileTestingTab,
  showModal,
  subscriptionValues,
  unifiedSubscriptions,
  updateProductConfig,
  usageSubscriptions,
  vdcConfig,
  vdcPlans,
  visibleModal,
  user,
}) => {
  const flags = useFlags();
  const history = useHistory();

  useEffect(() => {
    clearPurchaseData();
    initialize();
    if (legacySubscriptions.size === 0 && unifiedSubscriptions.length === 0) {
      trackEvent(
        EVENTS.PLANS_EMPTY_VISIT,
        flags?.webUiUsageBasedBilling ? MODE_USAGE : MODE_CONCURRENCY,
      );
      // Users without subscriptions can only see the PurchaseContainer,
      // so this is the only way for them to access their invoices
      fetchInvoices();
    } else {
      trackStandardEvent(
        STANDARD_EVENTS.PAGE_VIEWED,
        PRODUCT_AREA,
        PRODUCT_SUB_AREAS.PLANS,
        SUBJECT.CHANGE_SUBSCRIPTION_STATE,
      );
    }
  }, []);

  useEffect(() => {
    if (visibleModal === NUDGE_ANNUAL_PLAN_MODAL) hideModal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { ensureAccountPresent(); }, [ensureAccountPresent]);

  const isDetailedView = currentWebAndMobileTab !== null;

  const showComparisonMobileQuality = () => {
    trackEvent(EVENTS.PLANS_SEE_ALL, { product: 'mobilequality' });
    showModal(MOBILE_QUALITY_COMPARISON_MODAL);
  };

  const showComparisonLive = () => {
    trackEvent(EVENTS.PLANS_SEE_ALL, { product: 'live' });
    showModal(CROSS_BROWSER_FEATURES_MODAL);
  };

  const showComparisonAutomated = () => {
    trackEvent(EVENTS.PLANS_SEE_ALL, { product: 'automated' });
    showModal(CROSS_BROWSER_FEATURES_MODAL);
  };

  const onSliderValueChange = React.useCallback((value, product, addOnName = CONCURRENCY_KEY) => {
    trackEvent(EVENTS.PLANS_CHANGE_PARALLEL, { product, num_tests: value });
    updateProductConfig({ [addOnName]: value, product });
  }, []);
  const crumbList = isDetailedView ? [PARENT_CRUMB, { name: 'Update plan' }] : [PARENT_CRUMB];

  const onProductAdd = (product) => {
    trackEvent(EVENTS.PLANS_ADD_PRODUCT, { ...MODE_CONCURRENCY, product });
    if (product === PRODUCT_LIVE) {
      setWebAndMobileTestingTab(TESTING_TYPES.LIVE);
      if (liveConfig[CONCURRENCY_KEY] === 0) {
        updateProductConfig({ product, [CONCURRENCY_KEY]: 1 });
      }
    }
    if (product === PRODUCT_AUTOMATED) {
      setWebAndMobileTestingTab(TESTING_TYPES.AUTOMATED_LIVE);
      if (vdcConfig[CONCURRENCY_KEY] === 0) {
        updateProductConfig({ product: PRODUCT_VDC, [CONCURRENCY_KEY]: 1 });
      }
    }
  };

  const onMobileQualityToggle = (hasMobileQualityInCart) => {
    if (!hasMobileQualityInCart) {
      trackEvent(
        EVENTS.PLANS_ADD_PRODUCT,
        { ...MODE_CONCURRENCY, product: PRODUCT_MOBILE_QUALITY },
      );
    }

    updateProductConfig({
      [CONCURRENCY_KEY]: hasMobileQualityInCart ? 0 : 1,
      [INTERVAL_KEY]: PLAN_INTERVAL.YEAR,
      product: PRODUCT_MOBILE_QUALITY,
    });
  };

  const onApiTestingToggle = (hasApiTestingInCart) => {
    if (!hasApiTestingInCart) {
      trackEvent(EVENTS.PLANS_ADD_PRODUCT, { ...MODE_CONCURRENCY, product: PRODUCT_API_TESTING });
    }

    updateProductConfig({
      [PROJECTS_KEY]: hasApiTestingInCart ? 0 : ADDON_PROJECTS_PAID_THRESHOLD,
      [INTERVAL_KEY]: PLAN_INTERVAL.YEAR,
      product: PRODUCT_API_TESTING,
    });
  };

  const hasCrossBrowserSubscription = subscriptionValues[PRODUCT_LIVE]?.[CONCURRENCY_KEY] > 0 ||
    subscriptionValues[PRODUCT_VDC]?.[CONCURRENCY_KEY] > 0 ||
    subscriptionValues[PRODUCT_RDC]?.[CONCURRENCY_KEY] > 0;

  const hasMobileQualityInCart = mobileQualityConfig[CONCURRENCY_KEY] === 1;
  const hasMobileQualitySubscription = (
    subscriptionValues[PRODUCT_MOBILE_QUALITY]?.[CONCURRENCY_KEY] > 0
  );

  const apiTestingSubscriptionValue = subscriptionValues[PRODUCT_API_TESTING]
    ?.[PROJECTS_KEY];
  const hasApiTestingSubscription = apiTestingSubscriptionValue > 0;
  const hasApiTestingInCart = apiTestingConfig[PROJECTS_KEY] >= ADDON_PROJECTS_PAID_THRESHOLD;

  const resetOrder = () => {
    trackStandardEvent(
      STANDARD_EVENTS.OBJECT_CLICKED,
      PRODUCT_AREA,
      PRODUCT_SUB_AREAS.CHECKOUT,
      SUBJECT.RESET_ORDER,
    );
    setWebAndMobileTestingTab(null);
    orderItems.mapKeys((product) => {
      const addOnName = product === PRODUCT_API_TESTING ? PROJECTS_KEY : CONCURRENCY_KEY;
      updateProductConfig({ product, [addOnName]: 0 });
    });
  };

  const onContinueClick = () => {
    persistPurchaseData();
    trackEvent(EVENTS.PLANS_CONTINUE, {
      [PRODUCT_API_TESTING]: (
        orderItems.get(PRODUCT_API_TESTING)?.[PROJECTS_KEY] || 0
      ),
      [PRODUCT_LIVE]: orderItems.get(PRODUCT_LIVE)
        ?.[CONCURRENCY_KEY] || 0,
      [PRODUCT_MOBILE_QUALITY]:
        orderItems.get(PRODUCT_MOBILE_QUALITY)?.[CONCURRENCY_KEY] || 0,
      [PRODUCT_VDC]: orderItems.get(PRODUCT_VDC)?.[CONCURRENCY_KEY] || 0,
      [PRODUCT_RDC]: orderItems.get(PRODUCT_RDC)?.[CONCURRENCY_KEY] || 0,
    });
  };

  if (usageSubscriptions.size > 0 || flags?.webUiUsageBasedBilling) {
    return (<UsagePurchaseContainer />);
  }
  trackEvent(EVENTS.CONCURRENCY_PLANS_EMPTY_VISIT, MODE_CONCURRENCY);

  return (
    <>
      <Breadcrumbs
        list={crumbList}
        prefixComponent={<BackToPlatformLink />}
      >
        { hasInvoices ? <SideNavigation /> : null }
      </Breadcrumbs>
      <GridContainer>
        <PlansTitle />
        <main className="mt-3">
          <ContentLoader show={!initialized}>
            <Row>
              <Col
                lg={9}
                tagName="article"
              >
                <VStack gap={3}>
                  {!!(legacySubscriptions?.size) && (
                    <div className={LayoutStyles['subscriptions-list']}>
                      {legacySubscriptions.toSeq()
                        .map((sub) => (
                          <LegacyPlan
                            key={sub.id}
                            subscription={sub}
                          />
                        ))}
                    </div>
                  )}
                  {!!(unifiedSubscriptions?.length) && (
                    <UnifiedPlan subscriptions={unifiedSubscriptions} />
                  )}

                  <Loader
                    containerClass="m-auto text-center"
                    fontSize="3rem"
                    show={billingInfo.isLoading}
                  >
                    <AnimatePresence
                      exitBeforeEnter
                      initial={false}
                    >
                      <div>
                        {!isDetailedView ? (
                          <motion.div
                            key="plans"
                            animate="animate"
                            exit="exit"
                            initial="initial"
                            transition={TRANSITION}
                            variants={FADE}
                          >
                            <Box>
                              <LiveTile
                                onProductAdd={() => onProductAdd(PRODUCT_LIVE)}
                                price={prices.live}
                              />
                              <hr className="m-0 border-0 border-t border-solid border-gray-200" />
                              <AutomatedTile
                                onProductAdd={() => onProductAdd(PRODUCT_AUTOMATED)}
                                price={prices.automated}
                              />
                            </Box>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="plans-details"
                            animate="animate"
                            exit="exit"
                            initial="initial"
                            transition={TRANSITION}
                            variants={FADE}
                          >
                            <Tabs
                              key="plans-detail"
                              activeKey={currentWebAndMobileTab}
                              animate="animate"
                              animation={false}
                              className={TabsStyles.tabs}
                              defaultActiveKey="live"
                              exit="exit"
                              id="live-automated-tabs"
                              initial="initial"
                              mountOnEnter
                              onSelect={(tabName) => {
                                trackEvent(EVENTS.PLANS_SELECT_TAB, { product: tabName });
                                setWebAndMobileTestingTab(tabName);
                              }}
                              transition={TRANSITION}
                              unmountOnExit
                              variants={FADE}
                            >
                              <Tab
                                key={TESTING_TYPES.LIVE}
                                eventKey={TESTING_TYPES.LIVE}
                                tabClassName={TabsStyles.tab}
                                title={(
                                  <>
                                    <IconManualOutline className={TabsStyles['tab-icon']} />
                                    Live
                                  </>
                                )}
                              >
                                <Box>
                                  <Box.Section data-qa="product-section-cross-browser">
                                    <CrossBrowserFeaturesModal />
                                    <SectionTitle subtitle="Suitable if you or your team are new to cross-browser testing, or are non-technical.">
                                      <IconManualOutline className={Styles['icon-product']} />
                                      Live Testing
                                    </SectionTitle>
                                    <Row
                                      between="xs"
                                      className="mt-2"
                                    >
                                      <Col xs>
                                        <Box.InnerBox data-qa={`product-box-${PRODUCT_LIVE}`}>
                                          <Product.Header>
                                            <Product.Name>
                                              Manual Cross-Browser testing
                                            </Product.Name>
                                            <Product.IntervalChoice
                                              id={PRODUCT_LIVE}
                                              onChange={(isMonthly) => {
                                                updateProductConfig({
                                                  [INTERVAL_KEY]: (
                                                    getIntervalUnit(isMonthly)
                                                  ),
                                                  product: PRODUCT_LIVE,
                                                });
                                              }}
                                              saving="20%"
                                              selectedInterval={liveConfig[INTERVAL_KEY]}
                                            />
                                          </Product.Header>
                                          <Product.Slogan>
                                            Real &amp; virtual devices included
                                          </Product.Slogan>
                                          <Product.Description>
                                            <div className="flex flex-row justify-between">
                                              <Product.Concurrency
                                                allowedValues={ALLOWED_CONCURRENCY_VALUES}
                                                currentValue={liveConfig[CONCURRENCY_KEY]}
                                                subscriptionValue={subscriptionValues[PRODUCT_LIVE]
                                                  ?.[CONCURRENCY_KEY]}
                                                tooltipText="We recommend one parallel test per team member so they can run tests at the same time."
                                                updateConcurrency={(concurrency) => (
                                                  onSliderValueChange(concurrency, PRODUCT_LIVE)
                                                )}
                                              />
                                              <Product.MonthlyRate rate={livePlans[
                                                liveConfig[INTERVAL_KEY]]
                                                ?.calculateMonthlyRate(liveConfig[CONCURRENCY_KEY])}
                                              />
                                            </div>
                                          </Product.Description>
                                        </Box.InnerBox>
                                        <Row>
                                          <Col xs>
                                            <Button
                                              className="pt-0.5"
                                              color="primary"
                                              data-qa="live-features-modal-button"
                                              elementType="button"
                                              onClick={showComparisonLive}
                                              size="md"
                                              type="button"
                                              variant="text"
                                            >
                                              See what&apos;s included
                                            </Button>
                                          </Col>
                                          {!hasCrossBrowserSubscription && (
                                            <Col
                                              className={Styles['price-container']}
                                              xs
                                            >
                                              <Button
                                                className="mt-2"
                                                color="secondary"
                                                onClick={removeCrossBrowserConfig}
                                                size="md"
                                                variant="outline"
                                              >
                                                Remove Plan
                                              </Button>
                                            </Col>
                                          )}
                                        </Row>
                                      </Col>
                                    </Row>
                                  </Box.Section>
                                </Box>
                              </Tab>
                              <Tab
                                key={TESTING_TYPES.AUTOMATED_LIVE}
                                eventKey={TESTING_TYPES.AUTOMATED_LIVE}
                                tabClassName={TabsStyles.tab}
                                title={(
                                  <>
                                    <IconAutomatedOutline className={TabsStyles['tab-icon']} />
                                    <div className="ml-1 flex flex-col">
                                      {'Automated + Live'}
                                    </div>
                                  </>
                                )}
                              >
                                <Box>
                                  <Box.Section data-qa="product-section-cross-browser">
                                    <CrossBrowserFeaturesModal isAutomatedTab />
                                    <SectionTitle subtitle="Suitable for teams who are looking to automate their testing processes.">
                                      <IconAutomatedOutline className={`${Styles['icon-product']} color-gray-500`} />
                                      Automated + Live Testing
                                    </SectionTitle>
                                    <Row
                                      between="xs"
                                      className="mt-2"
                                    >
                                      <Col xs>
                                        <Box.InnerBox data-qa={`product-box-${PRODUCT_VDC}`}>
                                          <Product.Header>
                                            <Product.Name>Virtual Devices</Product.Name>
                                            <Product.IntervalChoice
                                              id={PRODUCT_VDC}
                                              onChange={(isMonthly) => {
                                                updateProductConfig({
                                                  [INTERVAL_KEY]: (
                                                    getIntervalUnit(isMonthly)
                                                  ),
                                                  product: PRODUCT_VDC,
                                                });
                                              }}
                                              saving="25%"
                                              selectedInterval={vdcConfig[INTERVAL_KEY]}
                                            />
                                          </Product.Header>
                                          <Product.Slogan>
                                            Cross-browser desktop &amp; mobile browser testing
                                          </Product.Slogan>
                                          <Product.Description>
                                            <div className="flex flex-row justify-between">
                                              <Product.Concurrency
                                                allowedValues={ALLOWED_CONCURRENCY_VALUES}
                                                currentValue={vdcConfig[CONCURRENCY_KEY]}
                                                subscriptionValue={subscriptionValues[PRODUCT_VDC]
                                                  ?.[CONCURRENCY_KEY]}
                                                tooltipText="More parallel tests means that your test suite will execute faster."
                                                updateConcurrency={(concurrency) => (
                                                  onSliderValueChange(concurrency, PRODUCT_VDC)
                                                )}
                                              />
                                              <Product.MonthlyRate rate={vdcPlans[
                                                vdcConfig[INTERVAL_KEY]]
                                                ?.calculateMonthlyRate(vdcConfig[CONCURRENCY_KEY])}
                                              />
                                            </div>
                                          </Product.Description>
                                        </Box.InnerBox>
                                        <Box.InnerBox data-qa={`product-box-${PRODUCT_RDC}`}>
                                          <Product.Header>
                                            <Product.Name>Real Devices</Product.Name>
                                            <Product.IntervalChoice
                                              id={PRODUCT_RDC}
                                              onChange={(isMonthly) => {
                                                updateProductConfig({
                                                  [INTERVAL_KEY]: (
                                                    getIntervalUnit(isMonthly)
                                                  ),
                                                  product: PRODUCT_RDC,
                                                });
                                              }}
                                              saving="20%"
                                              selectedInterval={rdcConfig[INTERVAL_KEY]}
                                            />
                                          </Product.Header>
                                          <Product.Slogan>
                                            Mobile app &amp; mobile browser testing
                                          </Product.Slogan>
                                          <Product.Description>
                                            <div className="flex flex-row justify-between">
                                              <Product.Concurrency
                                                allowedValues={ALLOWED_CONCURRENCY_VALUES}
                                                currentValue={rdcConfig[CONCURRENCY_KEY]}
                                                subscriptionValue={subscriptionValues[PRODUCT_RDC]
                                                  ?.[CONCURRENCY_KEY]}
                                                tooltipText="More parallel tests means that your test suite will execute faster."
                                                updateConcurrency={(concurrency) => (
                                                  onSliderValueChange(concurrency, PRODUCT_RDC)
                                                )}
                                              />
                                              <Product.MonthlyRate rate={rdcPlans[
                                                rdcConfig[INTERVAL_KEY]]
                                                ?.calculateMonthlyRate(rdcConfig[CONCURRENCY_KEY])}
                                              />
                                            </div>
                                          </Product.Description>
                                        </Box.InnerBox>
                                        <Row>
                                          <Col xs>
                                            <Button
                                              className="pt-0.5"
                                              color="primary"
                                              data-qa="automated-features-modal-button"
                                              elementType="button"
                                              onClick={showComparisonAutomated}
                                              size="md"
                                              type="button"
                                              variant="text"
                                            >
                                              See what&apos;s included
                                            </Button>
                                          </Col>
                                          {!hasCrossBrowserSubscription && (
                                            <Col
                                              className={Styles['price-container']}
                                              xs
                                            >
                                              <Button
                                                className="mt-2"
                                                color="secondary"
                                                elementType="button"
                                                onClick={removeCrossBrowserConfig}
                                                size="md"
                                                type="button"
                                                variant="outline"
                                              >
                                                <strong>Remove Plan</strong>
                                              </Button>
                                            </Col>
                                          )}
                                        </Row>
                                      </Col>
                                    </Row>
                                  </Box.Section>
                                </Box>
                              </Tab>
                            </Tabs>
                          </motion.div>
                        )}
                      </div>
                    </AnimatePresence>
                    <div>
                      <Box>
                        <ApiTestingTile
                          apiTestingConfig={apiTestingConfig}
                          apiTestingPlans={apiTestingPlans}
                          hasApiTestingInCart={hasApiTestingInCart}
                          hasApiTestingSubscription={hasApiTestingSubscription}
                          onApiTestingToggle={() => {
                            onApiTestingToggle(hasApiTestingInCart);
                          }}
                          onSliderValueChange={onSliderValueChange}
                          price={apiTestingPlans[apiTestingConfig[INTERVAL_KEY]]
                            ?.calculateMonthlyRate(ADDON_PROJECTS_PAID_THRESHOLD)}
                          subscriptionValues={subscriptionValues}
                          updateProductConfig={updateProductConfig}
                        />
                      </Box>
                    </div>
                    <div>
                      <Box>
                        <MobileQualityComparisonModal
                          handleTogglePlan={onMobileQualityToggle}
                          hasMobileQualityInCart={hasMobileQualityInCart}
                          hasMobileQualitySubscription={hasMobileQualitySubscription}
                          planPrice={mobileQualityPlans[mobileQualityConfig[INTERVAL_KEY]]
                            ?.calculateMonthlyRate()}
                        />
                        <MobileQualityTile
                          hasMobileQualityInCart={hasMobileQualityInCart}
                          hasMobileQualitySubscription={hasMobileQualitySubscription}
                          mobileQualityConfig={mobileQualityConfig}
                          mobileQualityPlans={mobileQualityPlans}
                          onMobileQualityToggle={() => {
                            onMobileQualityToggle(hasMobileQualityInCart);
                          }}
                          price={prices.mobileQuality}
                          showComparisonMobileQuality={showComparisonMobileQuality}
                          updateProductConfig={updateProductConfig}
                        />
                      </Box>
                    </div>
                  </Loader>
                </VStack>
              </Col>
              <Col
                className={LayoutStyles.aside}
                lg={3}
                tagName="aside"
              >
                <AnimatePresence
                  exitBeforeEnter
                  initial={false}
                >
                  {(isDetailedView || hasMobileQualityInCart || hasApiTestingInCart) && (
                    <motion.div
                      key="sidebar"
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      transition={TRANSITION}
                      variants={JUMP_FADE}
                    >
                      <Sticky
                        stickyStyle={{ marginTop: STICKY_SUMMARY_OFFSET }}
                        topOffset={-STICKY_SUMMARY_OFFSET}
                      >
                        <Box
                          className={OrderSummaryStyles['rounded-container']}
                        >
                          <Typography
                            className={OrderSummaryStyles['summary-heading']}
                            disableSpacing
                            variant="heading-4"
                          >
                            Order Summary
                          </Typography>
                          <OrderSummary
                            allPlans={allPlans}
                            currentMonthlyRate={currentMonthlyRate}
                            isContinueBlocked={isContinueBlocked}
                            onCancelClick={resetOrder}
                            onContinueClick={onContinueClick}
                            orderItems={orderItems}
                            showAnnualNudgeModal={() => {
                              const userId: string = user.getUserID();
                              const storeString = `${userId}:nudgeAnnualModal`;
                              const showNudgeAnnualModal = !localStorage.getItem(storeString);

                              // Only show the nudge modal once
                              if (showNudgeAnnualModal && getMonthlyPlans(orderItems).length > 0) {
                                trackStandardEvent(
                                  STANDARD_EVENTS.MESSAGE_SHOWN,
                                  PRODUCT_AREA,
                                  PRODUCT_SUB_AREAS.NUDGE_MODAL,
                                  SUBJECT.NUDGE_MODAL_VIEW,
                                );
                                localStorage.setItem(storeString, '{}');
                                showModal(NUDGE_ANNUAL_PLAN_MODAL);
                              } else {
                                onContinueClick();
                                history.push(`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT}`);
                              }
                            }}
                            updateToAnnual={() => {
                              getMonthlyPlans(orderItems).forEach((product) => {
                                updateProductConfig({
                                  [INTERVAL_KEY]: (
                                    getIntervalUnit(false)
                                  ),
                                  product,
                                });
                              });
                            }}
                          />
                        </Box>
                      </Sticky>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Col>
            </Row>
            <Row>
              <Col xs={9}>
                <hr className="border-0 border-t border-solid border-gray-300 mt-3 mb-2" />
                <div data-qa="additional-plans">
                  <div className="flex flex-row flex-wrap -ml-1 -mr-1">
                    <EnterprisePlansTile />
                    <OpenSauceTile />
                  </div>
                </div>
              </Col>
            </Row>
          </ContentLoader>
        </main>
      </GridContainer>
    </>
  );
};

PurchaseContainer.propTypes = {
  allPlans: PropTypes.arrayOf(PropTypes.instanceOf(Plan)).isRequired,
  apiTestingConfig: PropTypes.instanceOf(PlanConfiguration).isRequired,
  apiTestingPlans: PropTypes.shape({
    [PLAN_INTERVAL.MONTH]: PropTypes.instanceOf(Plan),
    [PLAN_INTERVAL.YEAR]: PropTypes.instanceOf(Plan),
  }),
  billingInfo: PropTypes.shape({
    isLoading: PropTypes.bool,
  }),
  clearPurchaseData: PropTypes.func.isRequired,
  currentMonthlyRate: PropTypes.number,
  currentWebAndMobileTab: PropTypes.oneOf([TESTING_TYPES.LIVE, TESTING_TYPES.AUTOMATED_LIVE]),
  hasInvoices: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  initialized: PropTypes.bool.isRequired,
  isContinueBlocked: PropTypes.bool,
  legacySubscriptions: ImmutablePropTypes.listOf(PropTypes.instanceOf(Subscription)),
  liveConfig: PropTypes.instanceOf(PlanConfiguration).isRequired,
  livePlans: PropTypes.shape({
    [PLAN_INTERVAL.MONTH]: PropTypes.instanceOf(Plan),
    [PLAN_INTERVAL.YEAR]: PropTypes.instanceOf(Plan),
  }),
  mobileQualityConfig: PropTypes.instanceOf(PlanConfiguration).isRequired,
  mobileQualityPlans: PropTypes.shape({
    [PLAN_INTERVAL.MONTH]: PropTypes.instanceOf(Plan),
    [PLAN_INTERVAL.YEAR]: PropTypes.instanceOf(Plan),
  }),
  orderItems: ImmutablePropTypes.mapOf(PropTypes.instanceOf(PlanConfiguration)).isRequired,
  persistPurchaseData: PropTypes.func.isRequired,
  prices: PropTypes.shape({
    apiTesting: PropTypes.number.isRequired,
    automated: PropTypes.number.isRequired,
    live: PropTypes.number.isRequired,
    mobileQuality: PropTypes.number.isRequired,
  }).isRequired,
  rdcConfig: PropTypes.instanceOf(PlanConfiguration).isRequired,
  rdcPlans: PropTypes.shape({
    [PLAN_INTERVAL.MONTH]: PropTypes.instanceOf(Plan),
    [PLAN_INTERVAL.YEAR]: PropTypes.instanceOf(Plan),
  }),
  removeCrossBrowserConfig: PropTypes.func.isRequired,
  setWebAndMobileTestingTab: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  subscriptionValues: PropTypes.shape({
    [PRODUCT_API_TESTING]: PropTypes.object,
    [PRODUCT_LIVE]: PropTypes.object,
    [PRODUCT_MOBILE_QUALITY]: PropTypes.object,
    [PRODUCT_RDC]: PropTypes.object,
    [PRODUCT_VDC]: PropTypes.object,
  }).isRequired,
  unifiedSubscriptions: PropTypes.arrayOf(PropTypes.instanceOf(Subscription)),
  updateProductConfig: PropTypes.func.isRequired,
  usageSubscriptions: ImmutablePropTypes.listOf(PropTypes.instanceOf(Subscription)),
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
  vdcConfig: PropTypes.instanceOf(PlanConfiguration).isRequired,
  vdcPlans: PropTypes.shape({
    [PLAN_INTERVAL.MONTH]: PropTypes.instanceOf(Plan),
    [PLAN_INTERVAL.YEAR]: PropTypes.instanceOf(Plan),
  }),
  visibleModal: PropTypes.string,
};

PurchaseContainer.defaultProps = {
  apiTestingPlans: {},
  billingInfo: {},
  currentMonthlyRate: null,
  currentWebAndMobileTab: null,
  isContinueBlocked: false,
  legacySubscriptions: null,
  livePlans: {},
  mobileQualityPlans: {},
  rdcPlans: {},
  unifiedSubscriptions: null,
  vdcPlans: {},
  visibleModal: null,
};

const mapStateToProps = (state) => ({
  ...selectors.purchaseContainerSelector(state),
  allPlans: plansSelectors.getNonLegacyPlans(state),
  apiTestingPlans: plansSelectors.getUnifiedApiTestingPlansSelector(state),
  billingInfo: billingInfoSelectors.billingInfoSelector(state),
  currentMonthlyRate: subscriptionsSelectors.currentMonthlyRateSelector(state),
  legacySubscriptions: subscriptionsSelectors.getLegacySubscriptionsSelector(state),
  livePlans: plansSelectors.getUnifiedLivePlansSelector(state),
  mobileQualityPlans: plansSelectors.getUnifiedMobileQualityPlansSelector(state),
  rdcPlans: plansSelectors.getUnifiedRdcPlansSelector(state),
  subscriptionValues: subscriptionsSelectors.getProductsFromSubscriptionSelector(state),
  unifiedSubscriptions: subscriptionsSelectors.getUnifiedSubscriptionsSelector(state),
  usageSubscriptions: subscriptionsSelectors.getUsageSubscriptionsSelector(state),
  vdcPlans: plansSelectors.getUnifiedVdcPlansSelector(state),
  prices: plansSelectors.getStartingPricesSelector(state),
  hasInvoices: !!state.apps.billing.invoices.get('elements').size,
  user: state.user,
  visibleModal: state.modal?.visible,
});

const mapDispatchToProps = {
  clearPurchaseData: operations.clearPurchaseData,
  ensureAccountPresent: billingInfoOperations.ensureAccountPresent,
  fetchInvoices: invoicesActions.fetchInvoicesAction,
  hideModal: modalActions.hideModal,
  initialize: operations.initialize,
  persistPurchaseData: operations.persistPurchaseData,
  removeCrossBrowserConfig: actions.removeCrossBrowserConfig,
  setWebAndMobileTestingTab: actions.setWebAndMobileTestingTab,
  showModal: modalActions.showModal,
  updateProductConfig: actions.updateProductConfig,
};

export { PurchaseContainer };
export default connect(mapStateToProps, mapDispatchToProps)(PurchaseContainer);
